import { makeAutoObservable, runInAction } from 'mobx'
import { ProductsService } from '~/shared/api/services/products'
import { AvailableProductModel } from '~/shared/api/services/products/types'
import { ReviewsService } from '~/shared/api/services/reviews'
import {
  ProductReviewApiStatus,
  ProductReviewsStore,
  ReviewType,
} from '~/shared/api/services/reviews/types'
import { ReviewsFilters, ReviewsLoaders } from './types'

const INITIAL_FILTERS: ReviewsFilters = {
  storeId: '',
  q: '',
  customerEmail: '',
  rating: '',
  productTitle: '',
  status: '',
  createdAt: '',
}

const RATING_OPTIONS = [1, 2, 3, 4, 5].map((rating) => ({
  label: `${rating}`,
  value: `${rating}`,
}))

const STATUS_OPTIONS = [
  {
    label: 'На модерации',
    value: '0',
  },
  {
    label: 'Отклонён',
    value: '2',
  },
  {
    label: 'Опубликован',
    value: '1',
  },
]

export class ReviewsStore {
  reviews: ReviewType[] = []

  stores: ProductReviewsStore[] = []

  productAvailabilities: AvailableProductModel[] = []

  ratingOptions = RATING_OPTIONS

  statusOptions = STATUS_OPTIONS

  filters: ReviewsFilters = INITIAL_FILTERS

  loaders: ReviewsLoaders = {
    reviews: false,
    loadMoreReviews: false,
    stores: false,
  }

  requestSettings = {
    limit: 20,
    offset: 0,
    isAllLoaded: false,
  }

  constructor() {
    makeAutoObservable(this)
  }

  setFilter = <K extends keyof ReviewsFilters>(
    key: K,
    value: ReviewsFilters[K],
  ) => {
    this.filters = {
      ...this.filters,
      [key]: value,
    }
  }

  setFilters = (filters: ReviewsFilters) => {
    this.filters = filters
  }

  updateReviewStatus = (id: string, status: ProductReviewApiStatus) => {
    this.reviews = this.reviews.map((review) =>
      review.id === id ? { ...review, status } : review,
    )
  }

  setReviewStatus = async (
    id: string,
    statusId: 0 | 1 | 2,
    rejectionReason?: string,
  ) => {
    await ReviewsService.setProductReviewStatus(id, {
      statusId,
      ...(rejectionReason ? { rejectionReason } : {}),
    })

    await this.getReviewStores()
    await this.getReviews()
  }

  getStoreProductAvailabilities = async () => {
    if (!this.filters.storeId) {
      this.productAvailabilities = []
      return []
    }

    const { data } = await ProductsService.getAvailableProducts({
      params: {
        storeId: this.filters.storeId,
      },
    })

    runInAction(() => {
      this.productAvailabilities = data
    })

    return data
  }

  getReviewStores = async () => {
    this.loaders = { ...this.loaders, stores: true }

    try {
      const { data } = await ReviewsService.getNotModeratedStores()

      runInAction(() => {
        this.stores = data

        if (!this.filters.storeId && data[0]?.id) {
          this.filters = {
            ...this.filters,
            storeId: data[0].id,
          }
        }
      })

      return data
    } finally {
      runInAction(() => {
        this.loaders = { ...this.loaders, stores: false }
      })
    }
  }

  getReviews = async (action?: 'loadMoreReviews') => {
    if (action === 'loadMoreReviews' && this.requestSettings.isAllLoaded) {
      return
    }

    this.loaders = { ...this.loaders, [action || 'reviews']: true }

    try {
      const params = {
        limit: this.requestSettings.limit,
        offset: action === 'loadMoreReviews' ? this.requestSettings.offset : 0,
        ...(this.filters.storeId ? { storeId: this.filters.storeId } : {}),
        ...(this.filters.q.trim() ? { q: this.filters.q.trim() } : {}),
        ...(this.filters.customerEmail.trim()
          ? { email: this.filters.customerEmail.trim() }
          : {}),
        ...(this.filters.rating ? { rating: this.filters.rating } : {}),
        ...(this.filters.productTitle
          ? { productAvailabilityId: this.filters.productTitle }
          : {}),
        ...(this.filters.status ? { statusId: this.filters.status } : {}),
        ...(this.filters.createdAt
          ? { createdAt: this.filters.createdAt }
          : {}),
      }

      const { data } = await ReviewsService.getProductReviews({ params })

      if (action === 'loadMoreReviews') {
        runInAction(() => {
          this.reviews = [...this.reviews, ...data]
        })
      } else {
        runInAction(() => {
          this.reviews = data
        })
      }

      runInAction(() => {
        this.requestSettings = {
          ...this.requestSettings,
          isAllLoaded: data.length < this.requestSettings.limit,
          offset:
            action === 'loadMoreReviews'
              ? this.requestSettings.offset + this.requestSettings.limit
              : this.requestSettings.limit,
        }
      })

      return data
    } finally {
      runInAction(() => {
        this.loaders = { ...this.loaders, [action || 'reviews']: false }
      })
    }
  }

  resetFilters = () => {
    this.filters = {
      ...INITIAL_FILTERS,
      storeId: this.filters.storeId,
    }
  }

  get productOptions() {
    return this.productAvailabilities
      .slice()
      .sort((a, b) => a.title.localeCompare(b.title))
      .map((productAvailability) => ({
        label: productAvailability.title,
        value: productAvailability.id,
      }))
  }

  get hasActiveFilters() {
    return Object.values({
      customerEmail: this.filters.customerEmail,
      rating: this.filters.rating,
      productTitle: this.filters.productTitle,
      status: this.filters.status,
      createdAt: this.filters.createdAt,
    }).some(Boolean)
  }

  get filteredReviews() {
    return this.reviews
  }
}
